import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  crearIdCategoria,
  normalizarTextoCategoria,
} from "../../utils/categorias";

const TURNOS = [
  { value: "matutino", label: "Matutino / Desayunos" },
  { value: "vespertino", label: "Vespertino" },
];

const obtenerNumeroOrden = (valor) => {
  const numero = Number(valor);

  if (!Number.isFinite(numero)) {
    return 0;
  }

  return Math.trunc(numero);
};

function AdminCategorias({ setMensaje }) {
  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [filtroTurno, setFiltroTurno] = useState("matutino");
  const [turnoFormulario, setTurnoFormulario] = useState("matutino");
  const [nombreCategoria, setNombreCategoria] = useState("");
  const [grupoCategoria, setGrupoCategoria] = useState("ALIMENTOS");
  const [ordenCategoria, setOrdenCategoria] = useState(1);
  const [categoriaEditando, setCategoriaEditando] = useState(null);

  const cargarDatos = async () => {
    setCargando(true);

    try {
      const [snapshotCategorias, snapshotProductos] = await Promise.all([
        getDocs(collection(db, "categorias")),
        getDocs(collection(db, "productos")),
      ]);

      const categoriasFirestore = snapshotCategorias.docs.map((documento) => ({
        id: documento.id,
        ...documento.data(),
      }));

      const productosFirestore = snapshotProductos.docs.map((documento) => ({
        id: documento.id,
        ...documento.data(),
      }));

      setCategorias(categoriasFirestore);
      setProductos(productosFirestore);
    } catch (error) {
      console.error("Error cargando categorías:", error);
      setMensaje?.("❌ Error al cargar categorías.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoriasDelTurnoFormulario = useMemo(() => {
    const turnoNormal = normalizarTextoCategoria(turnoFormulario);

    return categorias
      .filter((categoria) => {
        return normalizarTextoCategoria(categoria.turno) === turnoNormal;
      })
      .sort(
        (a, b) => obtenerNumeroOrden(a.orden) - obtenerNumeroOrden(b.orden),
      );
  }, [categorias, turnoFormulario]);

  const categoriasFiltradas = useMemo(() => {
    const turnoNormal = normalizarTextoCategoria(filtroTurno);

    return categorias
      .filter((categoria) => {
        return normalizarTextoCategoria(categoria.turno) === turnoNormal;
      })
      .sort(
        (a, b) => obtenerNumeroOrden(a.orden) - obtenerNumeroOrden(b.orden),
      );
  }, [categorias, filtroTurno]);

  const productosPorCategoria = useMemo(() => {
    const mapa = new Map();

    productos.forEach((producto) => {
      const turno = normalizarTextoCategoria(producto.turno || "matutino");
      const categoria = normalizarTextoCategoria(producto.seccion);

      if (!categoria) return;

      const clave = `${turno}__${categoria}`;

      mapa.set(clave, (mapa.get(clave) || 0) + 1);
    });

    return mapa;
  }, [productos]);

  const categoriasDetectadasSinConfigurar = useMemo(() => {
    const turnoFiltroNormal = normalizarTextoCategoria(filtroTurno);

    const configuradas = new Set(
      categorias
        .filter(
          (categoria) =>
            normalizarTextoCategoria(categoria.turno) === turnoFiltroNormal,
        )
        .map((categoria) => normalizarTextoCategoria(categoria.nombre)),
    );

    const detectadas = new Map();

    productos.forEach((producto) => {
      const turnoProducto = normalizarTextoCategoria(
        producto.turno || "matutino",
      );

      if (turnoProducto !== turnoFiltroNormal) return;

      const categoriaProducto = normalizarTextoCategoria(producto.seccion);

      if (!categoriaProducto) return;
      if (configuradas.has(categoriaProducto)) return;

      const clave = `${turnoProducto}__${categoriaProducto}`;

      if (!detectadas.has(clave)) {
        detectadas.set(clave, {
          turno: producto.turno || filtroTurno,
          nombre: categoriaProducto,
          cantidadProductos: 0,
        });
      }

      const actual = detectadas.get(clave);
      actual.cantidadProductos += 1;
    });

    return Array.from(detectadas.values()).sort((a, b) =>
      a.nombre.localeCompare(b.nombre, "es"),
    );
  }, [categorias, productos, filtroTurno]);

  const ordenMaximoPermitido = categoriaEditando
    ? categoriasDelTurnoFormulario.length
    : categoriasDelTurnoFormulario.length + 1;

  const categoriaConOrdenOcupado = categoriasDelTurnoFormulario.find(
    (categoria) =>
      categoria.id !== categoriaEditando?.id &&
      obtenerNumeroOrden(categoria.orden) ===
        obtenerNumeroOrden(ordenCategoria),
  );

  const limpiarFormulario = () => {
    setCategoriaEditando(null);
    setNombreCategoria("");
    setGrupoCategoria("ALIMENTOS");
    setTurnoFormulario(filtroTurno);
    setOrdenCategoria(categoriasFiltradas.length + 1);
  };

  useEffect(() => {
    if (!categoriaEditando) {
      setTurnoFormulario(filtroTurno);
      setOrdenCategoria(categoriasFiltradas.length + 1);
    }
  }, [filtroTurno, categoriasFiltradas.length, categoriaEditando]);

  const handleGuardarCategoria = async () => {
    const nombreNormal = normalizarTextoCategoria(nombreCategoria);
    const turnoNormal = normalizarTextoCategoria(turnoFormulario);
    const ordenNuevo = obtenerNumeroOrden(ordenCategoria);

    if (!nombreNormal) {
      setMensaje?.("⚠️ Escribe el nombre de la categoría.");
      return;
    }

    if (!turnoFormulario) {
      setMensaje?.("⚠️ Selecciona el turno de la categoría.");
      return;
    }

    if (!ordenNuevo || ordenNuevo < 1) {
      setMensaje?.("⚠️ El orden debe ser un número mayor o igual a 1.");
      return;
    }

    if (ordenNuevo > ordenMaximoPermitido) {
      setMensaje?.(
        `⚠️ No puedes brincar números. Para este turno el máximo permitido es ${ordenMaximoPermitido}.`,
      );
      return;
    }

    const idCategoria = crearIdCategoria(turnoFormulario, nombreNormal);

    if (categoriaEditando && idCategoria !== categoriaEditando.id) {
      setMensaje?.(
        "⚠️ Por seguridad, no cambiaremos el nombre/turno de una categoría existente. Crea una nueva categoría si necesitas otro nombre.",
      );
      return;
    }

    const categoriaMismoNombre = categoriasDelTurnoFormulario.find(
      (categoria) =>
        categoria.id !== categoriaEditando?.id &&
        normalizarTextoCategoria(categoria.nombre) === nombreNormal,
    );

    if (categoriaMismoNombre) {
      setMensaje?.(
        `⚠️ La categoría "${nombreNormal}" ya existe en este turno. Edita su orden en lugar de crearla otra vez.`,
      );
      return;
    }

    if (categoriaConOrdenOcupado) {
      const confirmarInsertar = window.confirm(
        `El orden ${ordenNuevo} ya está ocupado por "${categoriaConOrdenOcupado.nombre}". Si continúas, "${nombreNormal}" entrará en la posición ${ordenNuevo} y las categorías desde esa posición se recorrerán un número. ¿Continuar?`,
      );

      if (!confirmarInsertar) return;
    }

    setGuardando(true);
    setMensaje?.("");

    try {
      const batch = writeBatch(db);

      if (!categoriaEditando) {
        categoriasDelTurnoFormulario
          .filter(
            (categoria) => obtenerNumeroOrden(categoria.orden) >= ordenNuevo,
          )
          .forEach((categoria) => {
            const refCategoria = doc(db, "categorias", categoria.id);

            batch.update(refCategoria, {
              orden: obtenerNumeroOrden(categoria.orden) + 1,
              fechaActualizacion: serverTimestamp(),
            });
          });

        const refNuevaCategoria = doc(db, "categorias", idCategoria);

        batch.set(refNuevaCategoria, {
          nombre: nombreNormal,
          turno: turnoFormulario,
          grupo: grupoCategoria,
          orden: ordenNuevo,
          activa: true,
          fechaCreacion: serverTimestamp(),
          fechaActualizacion: serverTimestamp(),
        });
      } else {
        const ordenAnterior = obtenerNumeroOrden(categoriaEditando.orden);

        if (ordenNuevo < ordenAnterior) {
          categoriasDelTurnoFormulario
            .filter((categoria) => {
              const ordenActual = obtenerNumeroOrden(categoria.orden);

              return (
                categoria.id !== categoriaEditando.id &&
                ordenActual >= ordenNuevo &&
                ordenActual < ordenAnterior
              );
            })
            .forEach((categoria) => {
              const refCategoria = doc(db, "categorias", categoria.id);

              batch.update(refCategoria, {
                orden: obtenerNumeroOrden(categoria.orden) + 1,
                fechaActualizacion: serverTimestamp(),
              });
            });
        }

        if (ordenNuevo > ordenAnterior) {
          categoriasDelTurnoFormulario
            .filter((categoria) => {
              const ordenActual = obtenerNumeroOrden(categoria.orden);

              return (
                categoria.id !== categoriaEditando.id &&
                ordenActual <= ordenNuevo &&
                ordenActual > ordenAnterior
              );
            })
            .forEach((categoria) => {
              const refCategoria = doc(db, "categorias", categoria.id);

              batch.update(refCategoria, {
                orden: obtenerNumeroOrden(categoria.orden) - 1,
                fechaActualizacion: serverTimestamp(),
              });
            });
        }

        const refCategoriaEditada = doc(db, "categorias", categoriaEditando.id);

        batch.update(refCategoriaEditada, {
          nombre: nombreNormal,
          turno: turnoFormulario,
          grupo: grupoCategoria,
          orden: ordenNuevo,
          activa: true,
          fechaActualizacion: serverTimestamp(),
        });
      }

      await batch.commit();

      setMensaje?.(
        categoriaEditando
          ? "✅ Orden de categoría actualizado correctamente."
          : "✅ Categoría creada correctamente.",
      );

      limpiarFormulario();
      await cargarDatos();
    } catch (error) {
      console.error("Error guardando categoría:", error);
      setMensaje?.("❌ Error al guardar la categoría.");
    } finally {
      setGuardando(false);
    }
  };

  const handleEditarCategoria = (categoria) => {
    setCategoriaEditando(categoria);
    setTurnoFormulario(categoria.turno || "matutino");
    setNombreCategoria(normalizarTextoCategoria(categoria.nombre));
    setGrupoCategoria(categoria.grupo || "ALIMENTOS");
    setOrdenCategoria(obtenerNumeroOrden(categoria.orden));

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleEliminarCategoriaOrden = async (categoria) => {
    const turnoNormal = normalizarTextoCategoria(categoria.turno);
    const ordenEliminado = obtenerNumeroOrden(categoria.orden);

    const cantidadProductos = productosPorCategoria.get(
      `${turnoNormal}__${normalizarTextoCategoria(categoria.nombre)}`,
    );

    const confirmar = window.confirm(
      `Vas a eliminar la categoría "${categoria.nombre}" de la lista de orden.\n\nEsto NO borra productos. Solo elimina su configuración de orden y recorre las demás categorías.\n\nProductos detectados en esta categoría: ${
        cantidadProductos || 0
      }.\n\n¿Continuar?`,
    );

    if (!confirmar) return;

    setGuardando(true);
    setMensaje?.("");

    try {
      const categoriasMismoTurno = categorias
        .filter((item) => normalizarTextoCategoria(item.turno) === turnoNormal)
        .sort(
          (a, b) => obtenerNumeroOrden(a.orden) - obtenerNumeroOrden(b.orden),
        );

      const batch = writeBatch(db);

      batch.delete(doc(db, "categorias", categoria.id));

      categoriasMismoTurno
        .filter(
          (item) =>
            item.id !== categoria.id &&
            obtenerNumeroOrden(item.orden) > ordenEliminado,
        )
        .forEach((item) => {
          batch.update(doc(db, "categorias", item.id), {
            orden: obtenerNumeroOrden(item.orden) - 1,
            fechaActualizacion: serverTimestamp(),
          });
        });

      await batch.commit();

      if (categoriaEditando?.id === categoria.id) {
        limpiarFormulario();
      }

      setMensaje?.("✅ Categoría eliminada de la lista de orden.");
      await cargarDatos();
    } catch (error) {
      console.error("Error eliminando categoría:", error);
      setMensaje?.("❌ Error al eliminar la categoría.");
    } finally {
      setGuardando(false);
    }
  };

  const handleUsarDetectada = (categoriaDetectada) => {
    setCategoriaEditando(null);
    setTurnoFormulario(categoriaDetectada.turno || filtroTurno);
    setNombreCategoria(normalizarTextoCategoria(categoriaDetectada.nombre));
    setOrdenCategoria(categoriasFiltradas.length + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const labelTurno = TURNOS.find((turno) => turno.value === filtroTurno)?.label;

  return (
    <div>
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "18px",
          padding: "20px",
          marginBottom: "18px",
          border: "1px solid #eee",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
        }}
      >
        <h2
          style={{
            margin: "0 0 8px 0",
            fontSize: "22px",
            fontWeight: "900",
            color: "#111",
          }}
        >
          🗂️ Orden de categorías
        </h2>

        <p
          style={{
            margin: "0 0 16px 0",
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          Aquí defines el orden real en que aparecerán las categorías en la
          tienda. Si insertas una categoría en un número ocupado, las demás se
          recorren automáticamente.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "900",
                marginBottom: "6px",
              }}
            >
              Turno
            </label>

            <select
              value={turnoFormulario}
              onChange={(e) => {
                if (categoriaEditando) return;
                setTurnoFormulario(e.target.value);
              }}
              disabled={guardando || Boolean(categoriaEditando)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ddd",
                boxSizing: "border-box",
                backgroundColor: categoriaEditando ? "#f7f7f7" : "#fff",
              }}
            >
              {TURNOS.map((turno) => (
                <option key={turno.value} value={turno.value}>
                  {turno.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "900",
                marginBottom: "6px",
              }}
            >
              Categoría
            </label>

            <input
              type="text"
              value={nombreCategoria}
              onChange={(e) => setNombreCategoria(e.target.value.toUpperCase())}
              onBlur={() =>
                setNombreCategoria(normalizarTextoCategoria(nombreCategoria))
              }
              disabled={guardando || Boolean(categoriaEditando)}
              placeholder="Ej. PARA COMENZAR"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ddd",
                boxSizing: "border-box",
                backgroundColor: categoriaEditando ? "#f7f7f7" : "#fff",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "900",
                marginBottom: "6px",
              }}
            >
              Orden
            </label>

            <input
              type="number"
              min="1"
              max={ordenMaximoPermitido}
              value={ordenCategoria}
              onChange={(e) => setOrdenCategoria(e.target.value)}
              disabled={guardando}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ddd",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: "900",
                marginBottom: "6px",
              }}
            >
              Grupo
            </label>

            <select
              value={grupoCategoria}
              onChange={(e) => setGrupoCategoria(e.target.value)}
              disabled={guardando}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #ddd",
                boxSizing: "border-box",
              }}
            >
              <option value="ALIMENTOS">🍳 Alimentos</option>
              <option value="BEBIDAS">🥤 Bebidas</option>
            </select>
          </div>
        </div>

        {Number(ordenCategoria) > ordenMaximoPermitido && (
          <div
            style={{
              backgroundColor: "#fff4f4",
              border: "1px solid #ffd0d0",
              borderRadius: "12px",
              padding: "10px",
              marginBottom: "12px",
              fontSize: "13px",
              color: "#8b0000",
              fontWeight: "800",
            }}
          >
            ⚠️ No puedes brincar números. El máximo permitido para este turno es{" "}
            {ordenMaximoPermitido}.
          </div>
        )}

        {categoriaConOrdenOcupado && (
          <div
            style={{
              backgroundColor: "#fff8e1",
              border: "1px solid #ffe0a3",
              borderRadius: "12px",
              padding: "10px",
              marginBottom: "12px",
              fontSize: "13px",
              color: "#7a4b00",
              fontWeight: "800",
            }}
          >
            ⚠️ El orden {ordenCategoria} está ocupado por{" "}
            {categoriaConOrdenOcupado.nombre}. Si guardas, esa categoría y las
            siguientes se recorrerán.
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleGuardarCategoria}
            disabled={guardando || cargando}
            style={{
              backgroundColor: guardando || cargando ? "#bbb" : "#111",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "12px 16px",
              fontSize: "14px",
              fontWeight: "900",
              cursor: guardando || cargando ? "not-allowed" : "pointer",
            }}
          >
            {guardando
              ? "Guardando..."
              : categoriaEditando
                ? "Actualizar orden"
                : "Crear categoría"}
          </button>

          {categoriaEditando && (
            <button
              type="button"
              onClick={limpiarFormulario}
              disabled={guardando}
              style={{
                backgroundColor: "#eee",
                color: "#111",
                border: "none",
                borderRadius: "12px",
                padding: "12px 16px",
                fontSize: "14px",
                fontWeight: "900",
                cursor: guardando ? "not-allowed" : "pointer",
              }}
            >
              Cancelar edición
            </button>
          )}

          <button
            type="button"
            onClick={cargarDatos}
            disabled={guardando || cargando}
            style={{
              backgroundColor: "#f3f3f3",
              color: "#111",
              border: "none",
              borderRadius: "12px",
              padding: "12px 16px",
              fontSize: "14px",
              fontWeight: "900",
              cursor: guardando || cargando ? "not-allowed" : "pointer",
            }}
          >
            Actualizar
          </button>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "18px",
          padding: "20px",
          marginBottom: "18px",
          border: "1px solid #eee",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            marginBottom: "14px",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "900",
            }}
          >
            Categorías configuradas — {labelTurno}
          </h3>

          <select
            value={filtroTurno}
            onChange={(e) => setFiltroTurno(e.target.value)}
            disabled={guardando}
            style={{
              padding: "10px",
              borderRadius: "10px",
              border: "1px solid #ddd",
              fontWeight: "800",
            }}
          >
            {TURNOS.map((turno) => (
              <option key={turno.value} value={turno.value}>
                {turno.label}
              </option>
            ))}
          </select>
        </div>

        {cargando ? (
          <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
            Cargando categorías...
          </p>
        ) : categoriasFiltradas.length === 0 ? (
          <div
            style={{
              backgroundColor: "#fafafa",
              border: "1px dashed #ddd",
              borderRadius: "14px",
              padding: "16px",
              fontSize: "14px",
              color: "#666",
              fontWeight: "700",
            }}
          >
            Todavía no hay categorías configuradas para este turno.
          </div>
        ) : (
          <div style={{ display: "grid", gap: "10px" }}>
            {categoriasFiltradas.map((categoria) => {
              const cantidadProductos =
                productosPorCategoria.get(
                  `${normalizarTextoCategoria(
                    categoria.turno,
                  )}__${normalizarTextoCategoria(categoria.nombre)}`,
                ) || 0;

              return (
                <div
                  key={categoria.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "70px 1fr 120px 190px",
                    gap: "10px",
                    alignItems: "center",
                    border: "1px solid #eee",
                    borderRadius: "14px",
                    padding: "12px",
                    backgroundColor:
                      categoriaEditando?.id === categoria.id
                        ? "#fff8e1"
                        : "#fff",
                  }}
                >
                  <div
                    style={{
                      width: "46px",
                      height: "46px",
                      borderRadius: "999px",
                      backgroundColor: "#111",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      fontWeight: "900",
                    }}
                  >
                    {categoria.orden}
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "900",
                        color: "#111",
                      }}
                    >
                      {categoria.nombre}
                    </div>

                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        fontWeight: "700",
                      }}
                    >
                      Turno: {categoria.turno}
                    </div>

                    <div
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        fontWeight: "700",
                      }}
                    >
                      Grupo: {categoria.grupo || "ALIMENTOS"}
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      fontWeight: "800",
                    }}
                  >
                    {cantidadProductos} producto(s)
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleEditarCategoria(categoria)}
                      disabled={guardando}
                      style={{
                        backgroundColor: "#eee",
                        color: "#111",
                        border: "none",
                        borderRadius: "10px",
                        padding: "9px 10px",
                        fontSize: "12px",
                        fontWeight: "900",
                        cursor: guardando ? "not-allowed" : "pointer",
                      }}
                    >
                      Editar
                    </button>

                    <button
                      type="button"
                      onClick={() => handleEliminarCategoriaOrden(categoria)}
                      disabled={guardando}
                      style={{
                        backgroundColor: "#b00020",
                        color: "#fff",
                        border: "none",
                        borderRadius: "10px",
                        padding: "9px 10px",
                        fontSize: "12px",
                        fontWeight: "900",
                        cursor: guardando ? "not-allowed" : "pointer",
                      }}
                    >
                      Quitar orden
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "18px",
          padding: "20px",
          border: "1px solid #eee",
          boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
        }}
      >
        <h3
          style={{
            margin: "0 0 8px 0",
            fontSize: "18px",
            fontWeight: "900",
          }}
        >
          Categorías detectadas sin configurar
        </h3>

        <p
          style={{
            margin: "0 0 14px 0",
            fontSize: "13px",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          Estas categorías existen en productos, pero todavía no tienen un orden
          configurado. Puedes agregarlas al final y luego moverlas.
        </p>

        {categoriasDetectadasSinConfigurar.length === 0 ? (
          <div
            style={{
              backgroundColor: "#e8f5e9",
              border: "1px solid #c8e6c9",
              borderRadius: "14px",
              padding: "14px",
              color: "#1b5e20",
              fontSize: "13px",
              fontWeight: "800",
            }}
          >
            ✅ No hay categorías pendientes para este turno.
          </div>
        ) : (
          <div style={{ display: "grid", gap: "10px" }}>
            {categoriasDetectadasSinConfigurar.map((categoria) => (
              <div
                key={`${categoria.turno}-${categoria.nombre}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 130px 120px",
                  gap: "10px",
                  alignItems: "center",
                  border: "1px solid #eee",
                  borderRadius: "14px",
                  padding: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "900",
                      color: "#111",
                    }}
                  >
                    {categoria.nombre}
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      fontWeight: "700",
                    }}
                  >
                    Turno: {categoria.turno}
                  </div>
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    fontWeight: "800",
                  }}
                >
                  {categoria.cantidadProductos} producto(s)
                </div>

                <button
                  type="button"
                  onClick={() => handleUsarDetectada(categoria)}
                  disabled={guardando}
                  style={{
                    backgroundColor: "#111",
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "10px",
                    fontSize: "12px",
                    fontWeight: "900",
                    cursor: guardando ? "not-allowed" : "pointer",
                  }}
                >
                  Configurar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminCategorias;
